import React, { useEffect, useState, useMemo } from 'react'
import { FlatList, RefreshControl, Image, useWindowDimensions } from 'react-native'
import { ArrowLeft, Camera, Store } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp, useNavigationState } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import {
  Button,
  ButtonText,
  Card,
  EmptyState,
  Spinner,
  Text,
  TopBar,
  XStack,
  YStack,
} from '@fliisi/pollen'

const MAX_CARD_WIDTH = 200
const MIN_CARD_WIDTH = 150
const GRID_PADDING = 16
const CARD_GAP = 12

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'BoothView'>
  route: RouteProp<RootStackParamList, 'BoothView'>
}

interface Product {
  id: string
  title: string
  title_en: string | null
  title_fi: string | null
  price: number
  status: string
  product_images: { id: string; image_url: string }[]
}

interface BoothInfo {
  seller_name: string | null
  market_name: string
  space_name: string
  start_date: string
  end_date: string
}

export default function BoothViewScreen({ navigation, route }: Props) {
  const { reservationId, sellerId } = route.params
  const { t, language } = useLanguage()
  const { width } = useWindowDimensions()
  const [products, setProducts] = useState<Product[]>([])
  const [boothInfo, setBoothInfo] = useState<BoothInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const navIndex = useNavigationState((state) => state.index)
  const canGoBack = navIndex > 0

  const handleBack = () => {
    if (canGoBack) {
      navigation.goBack()
    } else {
      navigation.navigate('BuyerHome')
    }
  }

  const { columns, cardWidth, isDesktop } = useMemo(() => {
    const isDesktopView = width > 768
    const availableWidth = width - (GRID_PADDING * 2)

    if (isDesktopView) {
      const cols = Math.floor((availableWidth + CARD_GAP) / (MAX_CARD_WIDTH + CARD_GAP))
      const actualCols = Math.max(3, Math.min(cols, 6))
      const cardW = Math.min(MAX_CARD_WIDTH, (availableWidth - (actualCols - 1) * CARD_GAP) / actualCols)
      return { columns: actualCols, cardWidth: cardW, isDesktop: true }
    } else {
      const cardW = (availableWidth - CARD_GAP) / 2
      return { columns: 2, cardWidth: cardW, isDesktop: false }
    }
  }, [width])

  useEffect(() => {
    fetchData()
  }, [reservationId])

  const fetchData = async () => {
    setLoading(true)
    try {
      const { data: resData, error: resError } = await supabase
        .from('space_reservations')
        .select(`
          id,
          start_date,
          end_date,
          seller:users!space_reservations_seller_id_fkey (display_name, first_name, last_name),
          space:market_spaces (name),
          market:flea_markets (name)
        `)
        .eq('id', reservationId)
        .single()

      if (resError) throw resError

      const seller = Array.isArray(resData.seller) ? resData.seller[0] : resData.seller
      const space = Array.isArray(resData.space) ? resData.space[0] : resData.space
      const market = Array.isArray(resData.market) ? resData.market[0] : resData.market

      let sellerName = null
      if (seller) {
        sellerName = seller.display_name ||
          (seller.first_name || seller.last_name ? `${seller.first_name || ''} ${seller.last_name || ''}`.trim() : null)
      }

      setBoothInfo({
        seller_name: sellerName,
        market_name: market?.name || '',
        space_name: space?.name || '',
        start_date: resData.start_date,
        end_date: resData.end_date })

      const { data: prodData, error: prodError } = await supabase
        .from('products')
        .select(`
          id, title, title_en, title_fi, price, status,
          product_images (id, image_url)
        `)
        .eq('reservation_id', reservationId)
        .eq('status', 'for_sale')
        .order('created_at', { ascending: false })

      if (prodError) throw prodError
      setProducts(prodData || [])
    } catch (error) {
      console.error('Error fetching booth data:', error)
    } finally {
      setLoading(false)
    }
  }

  const onRefresh = async () => {
    setRefreshing(true)
    await fetchData()
    setRefreshing(false)
  }

  const getTitle = (product: Product) => {
    if (language === 'fi' && product.title_fi) return product.title_fi
    if (language === 'en' && product.title_en) return product.title_en
    return product.title || t('untitled')
  }

  const formatDateRange = () => {
    if (!boothInfo) return ''
    const start = new Date(boothInfo.start_date)
    const end = new Date(boothInfo.end_date)
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
    return `${start.toLocaleDateString(language === 'fi' ? 'fi-FI' : 'en-US', options)} - ${end.toLocaleDateString(language === 'fi' ? 'fi-FI' : 'en-US', options)}`
  }

  const renderProduct = ({ item }: { item: Product }) => {
    const imageUrl = item.product_images?.[0]?.image_url
    return (
      <YStack
        width={cardWidth}
        backgroundColor="$backgroundCard"
        borderRadius="$lg"
        overflow="hidden"
        elevation={3}
        onPress={() => navigation.navigate('ProductView', { productId: item.id })}
        pressStyle={{ opacity: 0.8, scale: 0.98 }}
        animation="quick"
        cursor="pointer"
      >
        <YStack width="100%" height={cardWidth} backgroundColor="$backgroundSecondary">
          {imageUrl ? (
            <Image source={{ uri: imageUrl }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
          ) : (
            <YStack f={1} jc="center" ai="center">
              <Camera size={32} color="#999" />
            </YStack>
          )}
        </YStack>
        <YStack padding="$sm">
          <Text fontSize={14} fontWeight="500" color="$color" marginBottom="$xs" numberOfLines={2} minHeight={36}>
            {getTitle(item)}
          </Text>
          <Text fontSize={16} fontWeight="bold" color="$colorPrimary">
            €{item.price?.toFixed(2) || '0.00'}
          </Text>
        </YStack>
      </YStack>
    )
  }

  if (loading) {
    return (
      <YStack f={1} backgroundColor="$background">
        <XStack
          justifyContent="space-between"
          alignItems="center"
          paddingHorizontal="$md"
          paddingTop="$md"
          paddingBottom="$md"
          backgroundColor="$backgroundCard"
          borderBottomWidth={1}
          borderBottomColor="$borderColor"
        >
          <XStack alignItems="center" onPress={handleBack} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer" paddingVertical="$sm" paddingRight="$md">
            <ArrowLeft size={18} color="var(--colorPrimary)" />
            <Text color="$colorPrimary" fontWeight="500" marginLeft="$xs">{t('back') || 'Back'}</Text>
          </XStack>
        </XStack>
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      </YStack>
    )
  }

  return (
    <YStack f={1} backgroundColor="$background">
      {/* Header */}
      <XStack
        justifyContent="space-between"
        alignItems="center"
        paddingHorizontal="$md"
        paddingTop="$md"
        paddingBottom="$md"
        backgroundColor="$backgroundCard"
        borderBottomWidth={1}
        borderBottomColor="$borderColor"
      >
        <XStack alignItems="center" onPress={handleBack} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer" paddingVertical="$sm" paddingRight="$md">
          <ArrowLeft size={18} color="var(--colorPrimary)" />
          <Text color="$colorPrimary" fontWeight="500" marginLeft="$xs">{t('back') || 'Back'}</Text>
        </XStack>
        <Text fontSize={18} fontWeight="bold" color="$color" f={1} textAlign="center">
          {boothInfo?.seller_name || t('booth') || 'Booth'}
        </Text>
        <YStack width={60} />
      </XStack>

      {/* Booth Info Banner */}
      {boothInfo && (
        <YStack backgroundColor="$backgroundCard" paddingHorizontal="$md" paddingVertical="$sm" borderBottomWidth={1} borderBottomColor="$borderColor">
          <XStack alignItems="center" flexWrap="wrap">
            <Text fontSize={16} fontWeight="600" color="$color">{boothInfo.market_name}</Text>
            <Text fontSize={16} color="$colorSecondary" marginLeft="$xs">· {boothInfo.space_name}</Text>
          </XStack>
          <Text fontSize={14} color="$colorSecondary" marginTop={2}>{formatDateRange()}</Text>
        </YStack>
      )}

      {/* Product Count */}
      <YStack backgroundColor="$colorPrimaryMuted" paddingHorizontal="$md" paddingVertical="$sm">
        <Text fontSize={14} color="$colorSecondary" fontWeight="500">
          {products.length} {products.length === 1 ? (t('product') || 'product') : (t('products') || 'products')}
        </Text>
      </YStack>

      {/* Products */}
      {products.length === 0 ? (
        <YStack f={1} jc="center" ai="center" padding="$3xl">
          <YStack width={80} height={80} borderRadius="$full" backgroundColor="$colorPrimaryMuted" jc="center" ai="center" marginBottom="$md">
            <Store size={48} color="var(--colorPrimary)" />
          </YStack>
          <Text fontSize={16} color="$colorSecondary" textAlign="center">
            {t('no_products_in_booth') || 'No products available in this booth'}
          </Text>
        </YStack>
      ) : (
        <FlatList
          data={products}
          renderItem={renderProduct}
          keyExtractor={(item) => item.id}
          numColumns={columns}
          key={columns}
          contentContainerStyle={{ padding: GRID_PADDING }}
          columnWrapperStyle={columns > 1 ? { gap: CARD_GAP, marginBottom: CARD_GAP } : undefined}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        />
      )}
    </YStack>
  )
}
